import type { MaterialTag } from "building-types/geometry/faceIteratorTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { FaceCategory, FaceGroupName } from "building-types/materials/faceCategories";
import type { InteriorMaterial } from "building-types/materials/interiorPaletteTypes";
import type { FBXUserPropertiesMaterialChoice } from "building-types/materials/materialTypes";
import type { PropMaterialOverrides } from "building-types/props/propTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { ViewEntityTable } from "building-types/views/viewEntityTable";
import type { ViewEntityType } from "building-types/views/viewEntityType";
import type { EntityDescriptor } from "building-types/views/viewEntityTypes";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { Geom } from "geom/geom";
import type { UVGeneration, UVTransformation } from "geom/uv";
import type { Vec3 } from "geom/vec3";
import type { DocEntityHandle } from "../handles/docEntityHandleTypes";

export enum BuildingViewerViewMode {
  INTERIOR = "INTERIOR",
  EXTERIOR = "EXTERIOR",
  PLAN = "PLAN",
  ELEVATION_OR_SECTION = "ELEVATION_OR_SECTION",
  WALL_PLANE = "WALL_PLANE",
  EXPLORE = "EXPLORE",
}

/**
 * Slot-to-slot material remapping for props.
 * Maps palette slot names to alternative slot names (e.g., door slots to trim slots).
 */
export type MaterialSlotRemapping = Partial<
  Record<ExteriorMaterial | InteriorMaterial, ExteriorMaterial | InteriorMaterial>
>;

export enum BuildingGeometryMode {
  BUILDING_VIEWER = "BUILDING_VIEWER",
  EXPORT = "EXPORT",
  LINE_RENDER = "LINE_RENDER",
  CROSS_SECTION = "CROSS_SECTION",
}

interface BuildingGeometryOptionsBase<T extends BuildingGeometryMode> {
  readonly mode: T;
}

export interface BuildingViewerGeometryOptions
  extends BuildingGeometryOptionsBase<BuildingGeometryMode.BUILDING_VIEWER> {
  readonly viewMode: BuildingViewerViewMode;
}

export interface NonBuildingViewerGeometryOptions
  extends BuildingGeometryOptionsBase<BuildingGeometryMode.EXPORT | BuildingGeometryMode.LINE_RENDER> {
  readonly addEntityIds?: boolean;
  readonly entityTable?: ViewEntityTable;
}

export type BuildingGeometryOptions = BuildingViewerGeometryOptions | NonBuildingViewerGeometryOptions;

export interface BuildingGeometry {
  readonly geom: Geom;
  readonly faceCategory: FaceCategory;
  readonly levelId?: string;
  readonly aboveLevel?: boolean;
  readonly entityId?: string;
  // entityHandleForSelectingReportingUnit can be used to select the reporting unit for the geometry.
  // It could be the id of some ancestor entity in case the entity itself doesn't have an entityId.
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  // reportingUnitLabelId takes precedence over entityHandleForSelectingReportingUnit if set.
  readonly reportingUnitLabelId?: string;

  // integerId undefined means that the object can't be picked, but still occludes other objects.
  // integerId INTEGER_ID_TRANSPARENT means that the object does not occlude anything for selection picking.
  readonly integerId?: number;

  readonly descriptor?: EntityDescriptor;

  readonly exportComponentName?: string;

  // Used to create new entity IDs when entityId/descriptor are not available
  readonly viewEntityType?: ViewEntityType;

  // Material properties
  readonly materialSlot?: MaterialTag;
  readonly materialSrc?: MaterialSrc;
  readonly uvGen: UVGeneration;
  readonly materialTransformation?: UVTransformation;

  // Studio rendering properties
  readonly threeLayers: number[];
  /** Whether this geometry contributes to generated cross-section poche. */
  readonly participatesInCrossSection?: boolean;
  readonly refractionProbePosition?: Vec3;
  readonly refractionProbeHalfSize?: Vec3;
  readonly dontClipWhenRenderedWithOverrideMaterial?: boolean;
  readonly overrideMaterialWithColor?: number; // This substitutes the entire material with a unlit flat color

  // BSP rendering properties
  readonly generateNeighbors?: boolean; // Defaults to false
}

/**
 * Prop instance data for emission to different targets (glTF manifest, THREE.js, etc.).
 * Uses the same `threeLayers` pattern as BuildingGeometry for consistency.
 */
export interface BuildingProp {
  // Rendering layers - same pattern as BuildingGeometry
  readonly threeLayers: number[];

  // Entity identification
  readonly integerId?: number;
  readonly entityId?: string;
  // entityHandleForSelectingReportingUnit can be used to select the reporting unit for the prop.
  // It could be the id of some ancestor entity in case the entity itself doesn't have an entityId.
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  // reportingUnitLabelId takes precedence over entityHandleForSelectingReportingUnit if set.
  readonly reportingUnitLabelId?: string;
  readonly levelId?: string;
  readonly aboveLevel?: boolean;
  readonly faceCategory: FaceCategory;

  // Prop asset reference
  readonly assetId: number | string;
  readonly src: string;

  // Transform
  readonly position: readonly [number, number, number];
  readonly rotationDegrees: number;
  readonly scale: readonly [number, number, number];

  // Material parameters (pre-merged catalog + prop overrides)
  readonly mergedMaterialOverrides?: PropMaterialOverrides;
  readonly useExteriorPalette?: boolean;

  /**
   * Room type for interior material variant selection.
   * Used by materialFromPalette to select room-specific material variants.
   */
  readonly roomType?: RoomType;

  /**
   * Pre-computed material slot remapping for rendering.
   * Used for door props where non-primary doors remap door materials to trim materials.
   * The THREE emitter applies this mapping; the GLTF emitter stores it in the manifest.
   */
  readonly materialRemapping?: MaterialSlotRemapping;
}

/**
 * Interface for emitting BuildingGeometry to different targets (arrays, glTF, etc.).
 * Provides a pluggable architecture for geometry processing.
 *
 * IMPORTANT: Always pass the emitter object to geometry functions, never extract
 * the emit method directly (e.g., `emitter.emit`) as this loses the `this` context.
 * The emit method is an arrow function property to safeguard against accidental unbinding.
 */
export interface BuildingGeometryEmitter {
  /** Emit a single geometry to the target */
  emit(geometry: BuildingGeometry): void;
  /** Optional: Emit a prop instance (3D model reference with transform) */
  emitProp?(prop: BuildingProp): void;
  /** Optional: Called when entering a named group (e.g., "Roofs", "Walls") */
  beginGroup?(name: FaceGroupName, sourceId?: string): void;
  /** Optional: Called when exiting a named group */
  endGroup?(name: FaceGroupName): void;
}

/**
 * Default emitter that collects BuildingGeometry into an array.
 * Uses arrow function property for emit to prevent `this` context loss.
 */
export class ArrayEmitter implements BuildingGeometryEmitter {
  readonly geometries: BuildingGeometry[] = [];
  readonly props: BuildingProp[] = [];

  /** Arrow function property ensures `this` is always bound correctly */
  emit = (geometry: BuildingGeometry): void => {
    this.geometries.push(geometry);
  };

  /** Arrow function property ensures `this` is always bound correctly */
  emitProp = (prop: BuildingProp): void => {
    this.props.push(prop);
  };
}

/**
 * Creates a wrapper emitter that adds additional properties to all emitted geometry.
 * This is useful for tagging geometry with levelId, aboveLevel, etc.
 *
 * @param baseEmitter - The underlying emitter to forward calls to
 * @param additionalProps - Properties to merge into every emitted geometry
 * @returns A new emitter that wraps the base emitter
 */
export function createWrappedEmitter(
  baseEmitter: BuildingGeometryEmitter,
  additionalProps: Partial<BuildingGeometry>,
): BuildingGeometryEmitter {
  return {
    // Forward all optional methods from the base emitter
    emitProp: baseEmitter.emitProp?.bind(baseEmitter),
    beginGroup: baseEmitter.beginGroup?.bind(baseEmitter),
    endGroup: baseEmitter.endGroup?.bind(baseEmitter),
    // Override emit to merge additional properties
    emit: (geometry: BuildingGeometry): void => {
      baseEmitter.emit({
        ...geometry,
        ...additionalProps,
      });
    },
  };
}

export function buildingViewerGeometryOptionsNew(viewMode: BuildingViewerViewMode): BuildingViewerGeometryOptions {
  return {
    mode: BuildingGeometryMode.BUILDING_VIEWER,
    viewMode,
  };
}

export function exportGeometryOptionsNew(): NonBuildingViewerGeometryOptions {
  return {
    mode: BuildingGeometryMode.EXPORT,
  };
}

export function lineRenderGeometryOptionsNew(
  addEntityIds: boolean,
  entityTable: ViewEntityTable | undefined,
): NonBuildingViewerGeometryOptions {
  return {
    mode: BuildingGeometryMode.LINE_RENDER,
    addEntityIds,
    entityTable,
  };
}

export function isBuildingPlanView(options: BuildingGeometryOptions): boolean {
  return options.mode === BuildingGeometryMode.BUILDING_VIEWER && options.viewMode === BuildingViewerViewMode.PLAN;
}

/**
 * Create a material mapping function from a MaterialSlotRemapping.
 * Used for door props where non-primary doors remap door materials to trim materials.
 */
export function createMaterialMappingFn(
  remapping: MaterialSlotRemapping,
): (material: FBXUserPropertiesMaterialChoice) => FBXUserPropertiesMaterialChoice {
  return (material: FBXUserPropertiesMaterialChoice): FBXUserPropertiesMaterialChoice => {
    if (typeof material === "string" && material in remapping) {
      return remapping[material as keyof typeof remapping] as FBXUserPropertiesMaterialChoice;
    }
    return material;
  };
}
